import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { InputDropdownSearchComponent } from '../inputs/input-dropdown-search/input-dropdown-search.component';
import { trigger, transition, style, animate } from '@angular/animations';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { VoucherService } from '../../../modules/finance/services/voucher.service';
import { TemplateListModalComponent } from 'src/app/modules/pda/components/templates/template-list-modal/template-list-modal.component';
import { TextAreaModalComponent } from 'src/app/shared/components/text-area-modal/text-area-modal.component';
import { InstructionModalComponent } from 'src/app/modules/pda/components/instruction/instruction-modal/instruction-modal.component';
import { VoucherModalComponent } from 'src/app/shared/modules/entities/voucher/voucher-modal/voucher-modal.component';
import { TemplatesService } from '../../../modules/finance/services/templates.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { finalize } from 'rxjs/operators';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import {
  CurrencyToggleAmounts,
  CurrencyToggleContext,
  CurrencyToggleService,
} from '../../services/currency-toggle.service';

@Component({
  selector: 'app-voucher-list',
  templateUrl: './voucher-list.component.html',
  styleUrls: ['./voucher-list.component.scss'],
  standalone: false,
  providers: [DialogService],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-6px)' }),
        animate(
          '180ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '150ms ease-in',
          style({ opacity: 0, transform: 'translateY(-6px)' }),
        ),
      ]),
    ]),
  ],
})
export class VoucherListComponent implements OnChanges, OnInit {
  private static readonly LOCAL_AMOUNT_SOURCE = 'local';
  private static readonly REFERENCE_AMOUNT_SOURCE = 'reference';

  /** The voucher array — supports two-way binding: [(vouchers)]="..." */
  @Input() vouchers: any[] = [];
  @Output() vouchersChange = new EventEmitter<any[]>();

  /** Exchange rate */
  @Input() exchange: number = 0;

  /** Currency IDs */
  @Input() idReferenceCurrency: number = 0;
  @Input() idLocalCurrency: number = 0;

  /** Full currency objects (replaces idReferenceCurrency/idLocalCurrency when provided).
   *  When set, the ids are derived from these objects for backward compatibility. */
  @Input() set localCurrency(val: any) {
    this._localCurrency = val;
    if (val?.id) this.idLocalCurrency = val.id;
  }
  get localCurrency(): any {
    return this._localCurrency;
  }
  private _localCurrency: any = null;

  @Input() set referenceCurrency(val: any) {
    this._referenceCurrency = val;
    if (val?.id) this.idReferenceCurrency = val.id;
  }
  get referenceCurrency(): any {
    return this._referenceCurrency;
  }
  private _referenceCurrency: any = null;

  /** Full currencies list (needed to set currencyName when toggling) */
  @Input() currencies: any[] = [];

  @Input() referenceField: string = 'currencyAmount';
  @Input() localField: string = 'localAmount';

  /** Voucher IDs whose amounts must NOT be recalculated when ROE changes (calculated by algorithm) */
  @Input() dependentVoucherIds: number[] = [];

  /** Quote ID to attach to newly added vouchers */
  @Input() quoteId: number | null = null;

  /**
   * 'quote'  → Add dropdown (Voucher + Template) + calculator button + reset button
   * 'template' → Add Voucher button + reset button (no calculator, no template option)
   */
  @Input() mode: 'quote' | 'template' = 'quote';

  /** When true, all inputs are read-only and add/remove buttons are hidden */
  @Input() disabled: boolean = false;

  /** Emitted whenever totals change */
  @Output() totalChanged = new EventEmitter<{ currencyAmount: number; localAmount: number }>();
  @Input() isFormDirty: boolean = false;
  @Output() remarkEditState = new EventEmitter<{
    voucherId: number | undefined;
    isDirty: boolean;
    wasDirtyBefore: boolean;
  }>();

  /** Emitted when the user clicks the Calculate button (quote mode only) */
  @Output() calculateRequested = new EventEmitter<void>();
  @Output() exchangeChange = new EventEmitter<number>();

  @ViewChild('quickAddDropdown')
  quickAddDropdown!: InputDropdownSearchComponent;

  voucherOptions: any[] = [];
  groupedVouchers: { typeName: string; vouchers: any[] }[] = [];
  quickAddVoucherId: any = null;

  private ref: DynamicDialogRef | undefined;
  /** Original snapshot passed from parent to compare edits against initial state */
  @Input() originalVouchers: { id: number | string; remark: string }[] = [];

  constructor(
    public dialogService: DialogService,
    private templatesService: TemplatesService,
    private spinnerService: NgxSpinnerService,
    private toastService: AppToastService,
    private voucherService: VoucherService,
    private currencyToggleService: CurrencyToggleService,
  ) {}

  ngOnInit(): void {
    this.loadVoucherOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vouchers']) {
      this.ensureVoucherAmountSources();
      this.regroup();
      if (+this.exchange > 0) {
        if (changes['vouchers'].firstChange) {
          // On initial load: only recalculate if some vouchers are missing their
          // derived amount (e.g. PDA import sets USD but leaves BRL as 0).
          if (this.hasMissingDerivedAmounts()) {
            setTimeout(() => this.recalculateAmounts(), 0);
          }
        } else {
          this.recalculateAmounts();
        }
      }
    }
    if (changes['referenceCurrency'] && !changes['referenceCurrency'].firstChange) {
      this.syncAllVoucherAmounts();
    }
    if (changes['exchange']) {
      if (changes['exchange'].firstChange) {
        // Exchange arrived first — check after a tick in case vouchers bind later.
        setTimeout(() => {
          if (+this.exchange > 0 && this.hasMissingDerivedAmounts()) {
            this.recalculateAmounts();
          }
        }, 0);
      } else {
        this.recalculateAmounts();
      }
    }
  }

  private hasMissingDerivedAmounts(): boolean {
    return this.vouchers.some((v) => {
      if (this.isReferenceCurrency(v)) {
        return (v[this.referenceField] ?? 0) > 0 && !(v[this.localField] ?? 0);
      }
      return (v[this.localField] ?? 0) > 0 && !(v[this.referenceField] ?? 0);
    });
  }

  // ─── Currency helpers ────────────────────────────────────────────────────────

  /** True when local and reference currencies are the same (single-currency mode). */
  get isSameCurrency(): boolean {
    if (!this._referenceCurrency) return true;
    return !!this.idLocalCurrency && this.idLocalCurrency === this.idReferenceCurrency;
  }

  // AB#312: a mecânica de toggle/conversão de moeda foi extraída pro CurrencyToggleService
  // (compartilhado com da-voucher-list e da-voucher-modal) — os métodos abaixo só adaptam
  // entre o shape do voucher deste componente (campos configuráveis via localField/
  // referenceField) e o CurrencyToggleAmounts genérico do service.

  private currencyContext(rate = +this.exchange || 0): CurrencyToggleContext {
    return {
      idLocalCurrency: this.idLocalCurrency,
      idReferenceCurrency: this.idReferenceCurrency,
      exchangeRate: rate,
      isSameCurrency: this.isSameCurrency,
    };
  }

  private toCurrencyState(voucher: any): CurrencyToggleAmounts {
    return {
      currencyId: voucher.currencyId,
      currencyName: voucher.currencyName,
      amountSource: voucher.amountSource,
      localAmount: voucher[this.localField],
      referenceAmount: voucher[this.referenceField],
    };
  }

  private applyCurrencyState(voucher: any, state: CurrencyToggleAmounts): void {
    voucher.currencyId = state.currencyId;
    voucher.amountSource = state.amountSource;
    voucher[this.localField] = state.localAmount;
    voucher[this.referenceField] = state.referenceAmount;
  }

  isReferenceCurrency(v: any): boolean {
    return this.currencyToggleService.isReferenceCurrency(
      { currencyId: v.currencyId, currencyName: v.currencyName },
      this.currencyContext(),
    );
  }

  getCurrencyPlaceholder(currency: any): string {
    const acronym = currency?.acronym?.toUpperCase();
    if (acronym === 'BRL') return 'R$ 0,00';
    if (acronym === 'USD') return '$ 0.00';
    return `${acronym || ''} 0.00`.trim();
  }

  setCurrencyById(voucher: any, currencyId: number): void {
    if (this.disabled) return;
    const state = this.currencyToggleService.setCurrencyById(
      this.toCurrencyState(voucher),
      currencyId,
      this.currencyContext(),
    );
    this.applyCurrencyState(voucher, state);
    // Also set the name so template-style isReferenceCurrency fallback works
    const found = this.currencies.find((c) => c.id === currencyId);
    if (found) voucher.currencyName = found.name;

    this.emitTotals();
    this.vouchersChange.emit(this.vouchers);
  }

  // ─── Amount input handlers ───────────────────────────────────────────────────

  onReferenceAmountInput(voucher: any, value: number): void {
    const state = this.currencyToggleService.onReferenceAmountInput(
      this.toCurrencyState(voucher),
      value,
      this.currencyContext(),
    );
    this.applyCurrencyState(voucher, state);
    this.emitTotals();
    this.vouchersChange.emit(this.vouchers);
  }

  onLocalAmountInput(voucher: any, value: number): void {
    const state = this.currencyToggleService.onLocalAmountInput(
      this.toCurrencyState(voucher),
      value,
      this.currencyContext(),
    );
    this.applyCurrencyState(voucher, state);
    this.emitTotals();
    this.vouchersChange.emit(this.vouchers);
  }

  // ─── Voucher CRUD ────────────────────────────────────────────────────────────

  addVoucher(): void {
    const localCurrency = this.currencies.find(
      (c) => c.id === this.idLocalCurrency,
    );
    const maxId =
      this.vouchers.length > 0
        ? Math.max(...this.vouchers.map((x) => x.id || 0))
        : 0;

    const newVoucher: any = {
      id: maxId + 1,
      voucherId: null,
      voucherName: null,
      voucherTypeName: null,
      voucherTypeId: null,
      status: 'I',
      currencyId: this.idLocalCurrency,
      amountSource: VoucherListComponent.LOCAL_AMOUNT_SOURCE,
      currencyName: localCurrency?.name || null,
      [this.referenceField]: null,
      [this.localField]: null,
    };
    if (this.quoteId) newVoucher.quoteId = this.quoteId;

    this.vouchers = [...this.vouchers, newVoucher];
    this.regroup();
    this.vouchersChange.emit(this.vouchers);
  }

  onVoucherSelected(row: any, voucherId: number | null): void {
    const v = voucherId
      ? this.voucherOptions.find((o) => o.id === voucherId)
      : null;
    row.voucherName = v?.name || null;
    row.voucherTypeName = v?.voucherTypeName || null;
    row.voucherTypeId = v?.voucherTypeId || null;
    // Only regroup immediately when a real voucher is selected.
    // When cleared (null), defer regroup to onVoucherBlur so the row
    // doesn't jump categories while the user is still typing.
    if (voucherId !== null) {
      this.regroup();
    }
    this.vouchersChange.emit(this.vouchers);
  }

  onVoucherBlur(): void {
    this.regroup();
  }

  openTemplateSearch(): void {
    this.ref = this.dialogService.open(TemplateListModalComponent, {
      showHeader: false,
      width: '900px',
      contentStyle: { padding: '0', borderRadius: '12px', overflow: 'hidden' },
      data: { title: 'Select Template' },
    });
    this.ref.onClose.subscribe((res: any) => {
      const templateSummary = res?.template || res;
      if (!templateSummary?.id) return;

      this.spinnerService.show();
      this.templatesService
        .getTemplate(templateSummary.id)
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe({
          next: (fullTemplate: any) => this.applyTemplate(fullTemplate),
          error: () =>
            this.toastService.error('Error', 'Error loading template details'),
        });
    });
  }

  private applyTemplate(tmpl: any): void {
    if (!tmpl) return;

    // If quote has no exchange rate yet but template does, propagate it to the parent
    const effectiveRate =
      +this.exchange > 0 ? +this.exchange : +tmpl.roe > 0 ? +tmpl.roe : 0;
    if (!+this.exchange && +tmpl.roe > 0) {
      this.exchangeChange.emit(+tmpl.roe);
    }

    const sourceVouchers = tmpl.voucherTemplates || [];
    const maxId =
      this.vouchers.length > 0
        ? Math.max(...this.vouchers.map((x) => x.id || 0))
        : 0;

    const newVouchers: any[] = sourceVouchers.map((v: any, i: number) => {
      const newV: any = {
        id: maxId + i + 1,
        voucherId: v.voucherId,
        voucherName: v.voucherName,
        remark: v.remark,
        voucherTypeId: v.voucherTypeId,
        voucherTypeName: v.voucherTypeName,
        status: 'I',
        currencyId: v.currencyId || this.idReferenceCurrency,
        [this.referenceField]: v.currencyAmount || 0,
        [this.localField]: v.localAmount || 0,
      };
      newV.amountSource = this.inferAmountSource(newV);
      if (this.quoteId) newV.quoteId = this.quoteId;

      if (effectiveRate > 0) this.syncVoucherAmountsFromSource(newV, effectiveRate);
      return newV;
    });

    this.vouchers = [...this.vouchers, ...newVouchers];
    this.regroup();
    this.vouchersChange.emit(this.vouchers);
  }

  quickAddVoucher(voucherId: number | null): void {
    if (!voucherId) return;

    // Clear and refocus the toolbar dropdown so the user can immediately type the next one
    this.quickAddDropdown?.clearAndFocus();

    const localCurrency = this.currencies.find(
      (c) => c.id === this.idLocalCurrency,
    );
    const maxId =
      this.vouchers.length > 0
        ? Math.max(...this.vouchers.map((x) => x.id || 0))
        : 0;
    const newVoucher: any = {
      id: maxId + 1,
      voucherId: voucherId,
      voucherName: null,
      voucherTypeName: null,
      voucherTypeId: null,
      status: 'I',
      currencyId: this.idLocalCurrency,
      amountSource: VoucherListComponent.LOCAL_AMOUNT_SOURCE,
      currencyName: localCurrency?.name || null,
      [this.referenceField]: null,
      [this.localField]: null,
    };
    if (this.quoteId) newVoucher.quoteId = this.quoteId;

    this.vouchers = [...this.vouchers, newVoucher];
    this.onVoucherSelected(newVoucher, voucherId);
    this.vouchersChange.emit(this.vouchers);
  }

  quickCreateAndAdd(): void {
    const ref = this.dialogService.open(VoucherModalComponent, {
      showHeader: false,
      width: '700px',
      contentStyle: { overflow: 'visible', padding: '0', borderRadius: '12px' },
      data: {},
    });
    ref.onClose.subscribe((newId: any) => {
      if (typeof newId !== 'number' || newId <= 0) return;
      this.loadVoucherOptions(() => this.quickAddVoucher(newId));
    });
  }

  removeVoucher(id: number): void {
    this.vouchers = this.vouchers.filter((v) => v.id !== id);
    this.regroup();
    this.vouchersChange.emit(this.vouchers);
  }

  removeAll(): void {
    const ref = this.dialogService.open(ConfirmationModalComponent, {
      showHeader: false,
      width: '450px',
      contentStyle: { padding: '0', borderRadius: '8px' },
      data: {
        title: `Remove all vouchers from this list?`,
        message: 'This action cannot be undone.',
        confirmLabel: 'Remove',
      },
    });
    ref.onClose.subscribe((confirmed: any) => {
      if (confirmed) {
        this.vouchers = [];
        this.regroup();
        this.vouchersChange.emit(this.vouchers);
      }
    });
  }

  // onDelete(id: number) {
  //     const ref = this.dialogService.open(ConfirmationModalComponent, {
  //       showHeader: false,
  //       width: '450px',
  //       contentStyle: { padding: '0', borderRadius: '8px' },
  //       data: {
  //         title: `Delete Quote #${id}?`,
  //         message: 'This action cannot be undone.',
  //         confirmLabel: 'Delete',
  //       },
  //     });

  //     ref.onClose.subscribe((confirmed: any) => {
  //       if (confirmed) {
  //         this.loading = true;
  //         this.quotesService.delete(id).subscribe({
  //           next: () => {
  //             this.toastService.success(
  //               'Deleted',
  //               `Quote #${id} has been removed.`,
  //             );
  //             this.loadAllQuotes();
  //           },
  //           error: () => {
  //             this.loading = false;
  //           },
  //         });
  //       }
  //     });
  //   }

  editRemark(voucher: any): void {
    const wasDirtyBefore = this.isFormDirty;
    // Baseline should come from the parent's initial snapshot, not the current live value.
    const snapshot = this.originalVouchers?.find((x) => x.id === voucher.id);
    const baselineText = snapshot?.remark ?? '';

    this.ref = this.dialogService.open(TextAreaModalComponent, {
      showHeader: false,
      width: '500px',
      contentStyle: { padding: '0', borderRadius: '12px' },
      data: {
        title: 'Edit Remark',
        text: voucher.remark || '',
        showSaveButton: false,
        instructionName: voucher.instructionId
          ? voucher.instructionName
          : undefined,
        onOpenInstruction: voucher.instructionId
          ? () => this.openInstructionModal(voucher.instructionId)
          : undefined,

        onTextChange: (newText: string) => {
          voucher.remark = newText;
          this.vouchersChange.emit(this.vouchers);

          const isDirty = newText !== baselineText;
          this.remarkEditState.emit({
            voucherId: voucher.id,
            isDirty,
            wasDirtyBefore,
          });
        },
      },
    });

    this.ref.onClose.subscribe((result: string) => {
      if (result !== undefined) {
        voucher.remark = result;
        this.vouchersChange.emit(this.vouchers);
      }
    });
  }

  private openInstructionModal(id: number): void {
    this.dialogService.open(InstructionModalComponent, {
      showHeader: false,
      width: '800px',
      contentStyle: { padding: '0', borderRadius: '12px' },
      data: { instructionId: id },
    });
  }

  // ─── Internal helpers ────────────────────────────────────────────────────────

  private loadVoucherOptions(onComplete?: () => void): void {
    this.voucherService.getVouchers(1000, 0, '').subscribe({
      next: (data: any) => {
        const list: any[] = (data.vouchers || []).filter(
          (v: any) => v.isActive,
        );
        this.voucherOptions = list.map((v) => ({
          ...v,
          displayName: v.voucherType?.name
            ? `${v.name} - ${v.voucherType.name}`
            : v.name,
          voucherTypeName: v.voucherType?.name || null,
          voucherTypeId: v.voucherType?.id || null,
        }));
        if (onComplete) onComplete();
      },
      error: () => {},
    });
  }

  openCreateVoucherModal(row: any): void {
    const ref = this.dialogService.open(VoucherModalComponent, {
      showHeader: false,
      width: '700px',
      contentStyle: { overflow: 'visible', padding: '0', borderRadius: '12px' },
      data: {},
    });

    ref.onClose.subscribe((newId: any) => {
      if (typeof newId !== 'number' || newId <= 0) return;
      this.loadVoucherOptions(() => {
        setTimeout(() => {
          row.voucherId = newId;
          this.onVoucherSelected(row, newId);
        }, 0);
      });
    });
  }

  private recalculateAmounts(): void {
    this.syncAllVoucherAmounts();
  }

  private regroup(): void {
    const map = new Map<string, any[]>();
    for (const v of this.vouchers || []) {
      const typeName = v.voucherTypeName || 'Others';
      if (!map.has(typeName)) map.set(typeName, []);
      map.get(typeName)!.push(v);
    }
    this.groupedVouchers = Array.from(map.entries()).map(
      ([typeName, vouchers]) => ({ typeName, vouchers }),
    );
    this.emitTotals();
  }

  private emitTotals(): void {
    const usd = (this.vouchers || []).reduce(
      (acc, v) => acc + (+(v[this.referenceField] ?? 0) || 0),
      0,
    );
    const real = (this.vouchers || []).reduce(
      (acc, v) => acc + (+(v[this.localField] ?? 0) || 0),
      0,
    );
    this.totalChanged.emit({ currencyAmount: usd, localAmount: real });
  }

  private syncAllVoucherAmounts(): void {
    const rate = +this.exchange || 0;
    this.ensureVoucherAmountSources();
    this.vouchers.forEach((v) => {
      if (this.dependentVoucherIds.includes(v.voucherId)) return;
      this.syncVoucherAmountsFromSource(v, rate);
    });
    this.emitTotals();
  }

  private ensureVoucherAmountSources(): void {
    (this.vouchers || []).forEach((v) => this.ensureAmountSource(v));
  }

  private ensureAmountSource(voucher: any): void {
    if (!voucher.amountSource) {
      voucher.amountSource = this.inferAmountSource(voucher);
    }
  }

  private inferAmountSource(voucher: any): string {
    return this.currencyToggleService.inferAmountSource(this.toCurrencyState(voucher), this.currencyContext());
  }

  private syncVoucherAmountsFromSource(voucher: any, rate = +this.exchange || 0): void {
    this.ensureAmountSource(voucher);
    const state = this.currencyToggleService.syncAmountsFromSource(
      this.toCurrencyState(voucher),
      this.currencyContext(rate),
    );
    this.applyCurrencyState(voucher, state);
  }
}
